import {
    DEFAULT_API_REQUEST_TIMEOUT,
    INTERNAL_SERVER_ERROR_STATUS_CODE,
    UNAUTHORIZED_STATUS_CODE,
    UNAVAILABLE_LEGAL_REASONS_STATUS_CODE,
} from '@constants/api.ts';
import { ROUTES } from '@router/constants.ts';
import { deleteAccessAndRefreshTokens, getClient, readToken } from '@store/services/localStorage.service.ts';
import { buildClientBaseUrl } from '@utils/clientExtractor.ts';
import { redirectToLoginWithCallbackUrl, redirectToServerErrorWithCallbackUrl } from '@utils/navigation.ts';
import type { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';

function getBaseURL(): string {
    return buildClientBaseUrl().replace(/\/$/, '');
}

export const httpApi = axios.create({
    baseURL: getBaseURL(),
    timeout: DEFAULT_API_REQUEST_TIMEOUT,
});

const clientRoutesPath = `/`;
export const clientLoginPath = `${clientRoutesPath}${ROUTES.AUTH.LOGIN}`;

httpApi.interceptors.request.use((cfg: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    if (!cfg.headers) {
        cfg.headers = {} as InternalAxiosRequestConfig['headers'];
    }
    // prevent "//auth/login" from becoming "http://auth/login"
    if (typeof cfg.url === 'string') {
        cfg.url = cfg.url.replace(/^\/{2,}/, '/'); // "///x" -> "/x"
    }

    cfg.headers.Authorization = `Bearer ${readToken()}`;
    return cfg;
});

// Request / response logging (dev only)
let debugReqId: number | null = null;
let debugResId: number | null = null;

export function setHttpDebug(on: boolean) {
    if (debugReqId !== null) httpApi.interceptors.request.eject(debugReqId);
    if (debugResId !== null) httpApi.interceptors.response.eject(debugResId);

    if (on) {
        debugReqId = httpApi.interceptors.request.use((cfg) => {
            console.debug('[http] →', cfg.method?.toUpperCase(), cfg.url, cfg.params ?? cfg.data);
            return cfg;
        });

        debugResId = httpApi.interceptors.response.use(
            (resp) => {
                console.debug('[http] ←', resp.config.url, resp.status, resp.data);
                return resp;
            },
            (err) => {
                console.log(err);
                const url = err.config?.url;
                const status = err?.status;
                const data = err?.data || 'No data';
                console.error('[http] ✗', url, status, data);
                return Promise.reject(err);
            },
        );
    }
}

// custom retry interceptor
const MAX_RETRIES = 0; // change to 1 or more to turn on re-try method
const RETRY_STATUSES = new Set([403, 408, 500, 502, 504]);

function getBackoffDelay(retryCount: number) {
    return Math.pow(2, retryCount) * 3000;
}

function isRetryableError(error: AxiosError) {
    if (!error.status) return false;

    return RETRY_STATUSES.has(error.status);
}

export const getClientAwarePath = (url: string) => {
    const client = getClient();
    const clean = (p: string) => `/${p.replace(/^\/+/, '')}`;
    const withNoClient = url.replace(new RegExp(`^/${client}/`, 'i'), '/');

    return clean(withNoClient);
};

httpApi.interceptors.response.use(
    (response) => response,
    async (error: AxiosError & { config?: AxiosRequestConfig & { __retryCount?: number } }) => {
        // We only need to redirect user from pages where valid token & consents are needed
        // Thereforwe we exclude login/registration pages in order to show proper messege when
        // login or password incorrect
        const pathName = window.location.pathname;
        const currentPageIsHomepage = pathName === clientRoutesPath;
        const excludePagesFromRedirection = [...Object.values(ROUTES.AUTH), ROUTES.DEFAULT.MAINTENANCE];
        const doesPageNeedValidTokenOrConsent =
            excludePagesFromRedirection.filter((p) => `${clientRoutesPath}${p}`.includes(pathName)).length === 0 ||
            currentPageIsHomepage;

        if (
            doesPageNeedValidTokenOrConsent &&
            (error.response?.status === UNAVAILABLE_LEGAL_REASONS_STATUS_CODE ||
                error.response?.status === UNAUTHORIZED_STATUS_CODE)
        ) {
            deleteAccessAndRefreshTokens();
            redirectToLoginWithCallbackUrl();
            return Promise.reject(error);
        }

        if (doesPageNeedValidTokenOrConsent && error.response?.status === INTERNAL_SERVER_ERROR_STATUS_CODE) {
            redirectToServerErrorWithCallbackUrl();
            return Promise.reject(error);
        }

        const cfg = error.config;
        if (!cfg) {
            return Promise.reject(error.response || error.message);
        }

        cfg.__retryCount = cfg.__retryCount ?? 0;

        if (cfg.__retryCount >= MAX_RETRIES || !isRetryableError(error)) {
            return Promise.reject(error.response || error.message);
        }

        cfg.__retryCount += 1;
        const delay = getBackoffDelay(cfg.__retryCount);
        await new Promise((res) => setTimeout(res, delay));
        return httpApi(cfg);
    },
);
